import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, onSnapshot, collectionGroup, where, doc, getDoc, getFirestore, getDocs } from 'firebase/firestore';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import app from '../../firebase/firebaseConfig';
import InfoIcon from '../common/InfoIcon';
import ExportModal from '../common/ExportModal';
import { useAuth } from '../../context/AuthContext';

const __app_id = app.options.appId;

const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const radius = outerRadius + 15;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const sin = Math.sin(-midAngle * RADIAN);
    const cos = Math.cos(-midAngle * RADIAN);
    const sx = cx + (outerRadius + 5) * cos;
    const sy = cy + (outerRadius + 5) * sin;
    const mx = cx + (outerRadius + 10) * cos;
    const my = cy + (outerRadius + 10) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 12;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
        <g>
            <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke="#888" fill="none" />
            <circle cx={ex} cy={ey} r={2} fill="#888" stroke="none" />
            <text x={ex + (cos >= 0 ? 1 : -1) * 6} y={ey} textAnchor={textAnchor} fill="#333">{`${name} (${(percent * 100).toFixed(0)}%)`}</text>
        </g>
    );
};

const HerdStatusPieChart = ({ chartData }) => {
    const COLORS = { "Milking": "#3b82f6", "Pregnant": "#8b5cf6", "Dry (Not Milking)": "#f59e0b", "Feeding": "#22c55e", "Sold": "#6b7280", "Dead": "#ef4444" };
    if (!chartData || chartData.length === 0) return null;

    return (
        <div className="mt-8 bg-white p-6 rounded-lg shadow flex flex-col">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Herd Status Overview</h3>
            <div className="flex-grow" style={{ height: '350px', width: '100%' }}>
                <ResponsiveContainer>
                    <PieChart margin={{ top: 10, right: 30, left: 30, bottom: 10 }}>
                        <Pie data={chartData} cx="50%" cy="50%" labelLine={false} outerRadius={100} fill="#8884d8" dataKey="value" nameKey="name" label={renderCustomizedLabel}>
                            {chartData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[entry.name] || '#cccccc'} />))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [`${value} animal(s)`, name]} />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

const Dashboard = ({ livestock, feedInventory }) => {
    const db = getFirestore(app);
    const { currentUser } = useAuth();
    const farmId = currentUser.farmId;

    // --- FIX: State for all transaction types ---
    const [manualTransactions, setManualTransactions] = useState([]);
    const [milkIncome, setMilkIncome] = useState([]);
    const [feedExpenses, setFeedExpenses] = useState([]);
    const [healthExpenses, setHealthExpenses] = useState([]);

    const [todayMilkRecords, setTodayMilkRecords] = useState([]);
    const [milkRecords30Days, setMilkRecords30Days] = useState([]);
    const [isExportModalOpen, setExportModalOpen] = useState(false);
    const chartRef = useRef(null);
    const [farmDetails, setFarmDetails] = useState({ name: 'Loading...', id: farmId });
    const [copySuccess, setCopySuccess] = useState('');

    useEffect(() => {
        if (!db || !farmId) return;

        const fetchFarmDetails = async () => {
            const farmRef = doc(db, 'farms', farmId);
            const farmSnap = await getDoc(farmRef);
            if (farmSnap.exists()) {
                setFarmDetails({ id: farmSnap.id, ...farmSnap.data() });
            }
        };
        fetchFarmDetails();

        // --- FIX: Fetch all transaction types for accurate profit calculation ---
        const transPath = `/artifacts/${__app_id}/farms/${farmId}/transactions`;
        const unsubTrans = onSnapshot(query(collection(db, transPath)), (snap) => setManualTransactions(snap.docs.map(d => d.data())));

        const fetchMilkIncome = async () => {
            const livestockPath = `artifacts/${__app_id}/farms/${farmId}/livestock`;
            const livestockSnapshot = await getDocs(query(collection(db, livestockPath)));
            const animals = livestockSnapshot.docs.map(doc => doc.id);

            const milkPromises = animals.map(async (animalId) => {
                const milkPath = `${livestockPath}/${animalId}/milk_records`;
                const milkSnapshot = await getDocs(collection(db, milkPath));
                return milkSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            });
            const allMilkRecords = (await Promise.all(milkPromises)).flat();
            
            const incomeFromMilk = allMilkRecords.map(record => {
                const dailyYield = (record.morningYield || 0) + (record.eveningYield || 0);
                const income = dailyYield * (record.pricePerLiter || 0);
                if (income <= 0) return null;
                return { date: record.date, type: 'income', amount: income };
            }).filter(Boolean);
            setMilkIncome(incomeFromMilk);
        };
        fetchMilkIncome();

        const feedPurchasesRef = collection(db, `/artifacts/${__app_id}/farms/${farmId}/feed_purchases`);
        const unsubFeed = onSnapshot(query(feedPurchasesRef), (snap) => {
            const expensesFromFeed = snap.docs.map(d => ({ date: d.data().date, type: 'expense', amount: d.data().totalCost }));
            setFeedExpenses(expensesFromFeed);
        });

        const healthRecordsRef = collectionGroup(db, 'health_records');
        const healthQuery = query(healthRecordsRef, where('farmId', '==', farmId), where('cost', '>', 0));
        const unsubHealth = onSnapshot(healthQuery, (snap) => {
            const expensesFromHealth = snap.docs.map(d => ({ date: d.data().date, type: 'expense', amount: d.data().cost }));
            setHealthExpenses(expensesFromHealth);
        });

        // Fetch milk yield for summary cards (this part is separate from profit calculation)
        const milkRecordsGroup = collectionGroup(db, 'milk_records');
        const todayStr = getLocalDateString(new Date());
        const todayQuery = query(milkRecordsGroup, where('farmId', '==', farmId), where('date', '==', todayStr));
        const unsubTodayMilk = onSnapshot(todayQuery, (snap) => setTodayMilkRecords(snap.docs.map(d => d.data())));
        
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const thirtyDaysAgoStr = getLocalDateString(thirtyDaysAgo);
        const thirtyDayQuery = query(milkRecordsGroup, where('farmId', '==', farmId), where('date', '>=', thirtyDaysAgoStr));
        const unsub30DayMilk = onSnapshot(thirtyDayQuery, (snap) => setMilkRecords30Days(snap.docs.map(d => d.data())));
        
        return () => { unsubTrans(); unsubTodayMilk(); unsub30DayMilk(); unsubFeed(); unsubHealth(); };
    }, [db, farmId]);

    const handleCopyId = () => {
        navigator.clipboard.writeText(farmId).then(() => {
            setCopySuccess('Copied!');
            setTimeout(() => setCopySuccess(''), 2000);
        }, () => {
            setCopySuccess('Failed to copy');
            setTimeout(() => setCopySuccess(''), 2000);
        });
    };

    // --- FIX: Combine all transactions before calculating profit ---
    const allTransactions = useMemo(() => {
        return [...manualTransactions, ...milkIncome, ...feedExpenses, ...healthExpenses];
    }, [manualTransactions, milkIncome, feedExpenses, healthExpenses]);

    // --- CALCULATIONS ---
    const activeLivestock = livestock.filter(animal => animal.status !== 'Sold' && animal.status !== 'Dead').length;
    const totalEverOwned = livestock.length;
    const todayTotalMilk = todayMilkRecords.reduce((acc, rec) => acc + (rec.morningYield || 0) + (rec.eveningYield || 0), 0);
    const totalMilk30Days = milkRecords30Days.reduce((acc, rec) => acc + (rec.morningYield || 0) + (rec.eveningYield || 0), 0);
    const lowStockItems = feedInventory.filter(item => item.stock <= item.lowStockThreshold).length;
    
    // --- FIX: Use the combined transaction list for profit calculations ---
    const netProfitAllTime = allTransactions.reduce((acc, t) => acc + (t.type === 'income' ? t.amount : -t.amount), 0);
    const last30DaysDate = new Date();
    last30DaysDate.setDate(last30DaysDate.getDate() - 30);
    const recentTransactions = allTransactions.filter(t => new Date(t.date) >= last30DaysDate);
    const netProfit30Days = recentTransactions.reduce((acc, t) => acc + (t.type === 'income' ? t.amount : -t.amount), 0);

    const statusChartData = useMemo(() => {
        const counts = {};
        livestock.forEach(animal => {
            const status = animal.status || "Unknown";
            counts[status] = (counts[status] || 0) + 1;
        });
        return Object.entries(counts).map(([name, value]) => ({ name, value }));
    }, [livestock]);

    const statusCountsForExport = statusChartData.reduce((acc, item) => {
        acc[`Status: ${item.name}`] = item.value;
        return acc;
    }, {});

    const summaryDataForExport = {
        "Active Livestock": activeLivestock,
        "Total Milk (Today)": todayTotalMilk.toFixed(1),
        "Low Stock Items": lowStockItems,
        "Net Profit (30 Days)": netProfit30Days.toFixed(2),
        ...statusCountsForExport,
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-gray-800">{farmDetails.name.toUpperCase()}</h2>
                <button onClick={() => setExportModalOpen(true)} className="px-4 py-2 bg-gray-600 text-white rounded-md">Export Data</button>
            </div>
            
            <div className="mb-6 bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-r-lg" hidden>
                <h3 className="text-lg font-semibold text-indigo-800">{farmDetails.name}</h3>
                <p className="text-sm text-gray-600 mt-1">Share this ID with new users to have them join your farm:</p>
                <div className="mt-2 flex items-center bg-white p-2 rounded-md shadow-sm">
                    <input type="text" readOnly value={farmDetails.id} className="flex-grow p-1 border-none bg-transparent focus:ring-0 text-sm text-gray-700" />
                    <button onClick={handleCopyId} className="ml-2 px-3 py-1 bg-indigo-500 text-white text-xs font-bold rounded-md hover:bg-indigo-600">
                        {copySuccess || 'Copy ID'}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg shadow flex flex-col justify-between">
                    <div>
                        <p className="text-xs text-gray-500">Total Ever Owned: {totalEverOwned}</p>
                        <div className="flex justify-between items-start mt-1">
                            <p className="text-sm font-medium text-gray-700">Active Livestock</p>
                            <InfoIcon tooltip="Total animals excluding those marked as 'Sold' or 'Dead'."/>
                        </div>
                    </div>
                    <p className="mt-1 text-3xl font-semibold text-gray-900">{activeLivestock}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow flex flex-col justify-between">
                    <div>
                        <p className="text-xs text-gray-500">Last 30 Days Total: {totalMilk30Days.toFixed(1)} L</p>
                        <div className="flex justify-between items-start mt-1">
                            <p className="text-sm font-medium text-gray-700">Total Milk Yield (Today)</p>
                            <InfoIcon tooltip="The total milk produced by all active animals today."/>
                        </div>
                    </div>
                    <p className="mt-1 text-3xl font-semibold text-blue-600">{todayTotalMilk.toFixed(1)} L</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow flex flex-col justify-between">
                     <div>
                        <div className="flex justify-between items-start">
                            <p className="text-sm font-medium text-gray-700">Low Stock Feed Items</p>
                            <InfoIcon tooltip="Number of feed items below their low-stock threshold."/>
                        </div>
                    </div>
                    <p className={`mt-1 text-3xl font-semibold ${lowStockItems > 0 ? 'text-red-600' : 'text-gray-900'}`}>{lowStockItems}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow flex flex-col justify-between">
                    <div>
                        <p className="text-xs text-gray-500">All-Time Profit: ₹{netProfitAllTime.toFixed(2)}</p>
                        <div className="flex justify-between items-start mt-1">
                            <p className="text-sm font-medium text-gray-700">Net Profit (30 Days)</p>
                            <InfoIcon tooltip="Total income minus total expenses in the last 30 days."/>
                        </div>
                    </div>
                    <p className={`mt-1 text-3xl font-semibold ${netProfit30Days >= 0 ? 'text-green-600' : 'text-red-600'}`}>₹{netProfit30Days.toFixed(2)}</p>
                </div>
            </div>
            
            <div ref={chartRef}>
                <HerdStatusPieChart chartData={statusChartData} />
            </div>

            <ExportModal 
                isOpen={isExportModalOpen} 
                onClose={() => setExportModalOpen(false)} 
                data={summaryDataForExport}
                chartRef={chartRef}
            />
        </div>
    );
};

export default Dashboard;
